import { useState, useEffect, useRef, useCallback } from 'react';
import { adminAPI } from '../services/api';
import type { UserSuggestion } from '../types';

const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 3;

interface UseUserSuggestResult {
  suggestions: UserSuggestion[];
  loading: boolean;
  clearSuggestions: () => void;
}

export function useUserSuggest(query: string): UseUserSuggestResult {
  const [suggestions, setSuggestions] = useState<UserSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
  }, []);

  useEffect(() => {
    // Clear pending timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    // Cancel in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // Below minimum length — clear and bail
    if (query.trim().length < MIN_QUERY_LENGTH) {
      setSuggestions([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    timerRef.current = setTimeout(async () => {
      const controller = new AbortController();
      abortControllerRef.current = controller;

      try {
        const response = await adminAPI.suggestUsers(query.trim(), controller.signal);
        if (!controller.signal.aborted) {
          setSuggestions(response.data.data || []);
        }
      } catch (err: any) {
        if (err?.name !== 'CanceledError' && err?.code !== 'ERR_CANCELED') {
          console.error('[useUserSuggest] Error fetching suggestions:', err);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [query]);

  return { suggestions, loading, clearSuggestions };
}
